import { SkeletonText } from '@walmart-web/livingdesign-components';
import '@walmart-web/livingdesign-components/index.esm.css';
import Cookies from 'js-cookie';
import React from 'react';
import i18n from './i18n'; // initialize i18n and import instance
import { useQueryClient } from 'react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import AcntMetricsDashboard from './components/acntMetricsDashboard/acntMetricsDashboard';
import PageContent from './components/commonComponents/pageContainer';
import PageContentReviewerDashboard from './components/commonComponents/pageContainer/indexReviewerDashboard';
import TopBar from './components/commonComponents/topBar/topbar';
import {
  ACNT_ADMIN_ROLE,
  ACNT_ASSOCIATE_ROLE,
  ACNT_FINANCE_ROLE,
  ACNT_REVIEWER_ROLE,
  ACNT_SUPPORT_TIER_1,
  ACNT_SUPPORT_TIER_2,
  ADMIN_HOME_PAGE,
  REVIEWER_HOME_PAGE,
} from './constants';
import { getVerifiedRole } from './utils/index';
import Chat from './components/chat/Chat';

export const Home = React.lazy(() => import(/* webpackChunkName: "Home" */ './scenes/home/home'));

export const RouteToHomePage = () => {
  const roles = Cookies.get('userRole');
  if (
    roles?.includes(ACNT_ASSOCIATE_ROLE) ||
    roles?.includes(ACNT_FINANCE_ROLE) ||
    roles?.includes(ACNT_SUPPORT_TIER_1) ||
    roles?.includes(ACNT_SUPPORT_TIER_2)
  ) {
    return <Home />;
  }
  if (roles?.includes(ACNT_REVIEWER_ROLE)) {
    return <Navigate to={REVIEWER_HOME_PAGE} />;
  }
  if (roles?.includes(ACNT_ADMIN_ROLE)) {
    return <Navigate to={ADMIN_HOME_PAGE} />;
  }
  return <Home />;
};

export const ApplicationDetailsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "ApplicationDetailsPage" */ './scenes/applicationDetailsPage/applicationDetailsPage')
);
export const NewGrantApplicationPage = React.lazy(
  () => import(/* webpackChunkName: "NewGrantApplicationPage" */ './scenes/newGrantApplication/newGrantApplicationPage')
);
export const CreateHardship = React.lazy(
  () => import(/* webpackChunkName: "CreateHardship" */ './components/commonComponents/HardshipEvent/CreateHardship')
);
export const CreateGrantRequest = React.lazy(
  () =>
    import(/* webpackChunkName: "CreateGrantRequest" */ './components/commonComponents/grantRequest/CreateGrantRequest')
);
export const NewGrantReviewSubmitPage = React.lazy(
  () =>
    import(/* webpackChunkName: "NewGrantReviewSubmitPage" */ './scenes/newGrantReviewSubmit/newGrantReviewSubmitPage')
);
const AdminHome = React.lazy(() => import(/* webpackChunkName: "AdminHome" */ './scenes/adminHome/adminHome'));
export const CommunicationPreferences = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CommunicationPreferences" */
      './components/commonComponents/communicationPreferences/CommunicationPreferences'
    )
);
export const ReviewerDashboard = React.lazy(
  () => import(/* webpackChunkName: "ReviewerDashboard" */ './scenes/reviewerDashboard/reviewerDashboard')
);
export const ConfirmationPage = React.lazy(
  () =>
    import(/* webpackChunkName: "ConfirmationPage" */ './components/commonComponents/confirmationPage/congratulations')
);
export const GrantDetailsPage = React.lazy(
  () => import(/* webpackChunkName: "GrantDetailsPage" */ './components/grantDetailsPage/grantDetails')
);
export const ReviewerApplicationDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ReviewerApplicationDetails" */
      './components/reviewer/reviewerApplicationDetails/ReviewerApplicationDetails'
    )
);

export const ReviewerFilesPreview = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ReviewerFilesPreview" */
      './components/reviewer/reviewerApplicationDetails/reviewerFilesPreview/ReviewerFilesPreview'
    )
);

export const CreateDisaster = React.lazy(
  () => import(/* webpackChunkName: "CreateDisaster" */ './components/disaster/CreateDisaster')
);
export const FinanceReport = React.lazy(
  () => import(/* webpackChunkName: "FinanceReport" */ './components/financeReport/financeReports')
);
export const NotFoundPage = React.lazy(
  () => import(/* webpackChunkName: "NotFoundPage" */ './components/NotFound/notFoundPage')
);
export const ErrorMessageComp = React.lazy(
  () => import(/* webpackChunkName: "ErrorMessageComp" */ './components/NotFound/ErrorMessage')
);
export const Health = React.lazy(() => import(/* webpackChunkName: "Health" */ './components/health/Health'));

const acntAssociateRoutes = (
  <Route path="/" key="associate">
    <Route path="" element={<PageContent><Home /></PageContent>} />
    <Route path="/application-details" element={<PageContent><ApplicationDetailsPage /></PageContent>} />
    <Route path="/newgrant-application" element={<PageContent><NewGrantApplicationPage /></PageContent>} />
    <Route path="/review-submit" element={<PageContent><NewGrantReviewSubmitPage /></PageContent>} />
    <Route path="/confirmation" element={<PageContent><ConfirmationPage /></PageContent>} />
    <Route path="/grantdetails" element={<PageContent><GrantDetailsPage /></PageContent>} />
    <Route path="/preferences" element={<PageContent><CommunicationPreferences type="editGrant" /></PageContent>} />
    <Route path="associate">
      <Route path="*" element={<Navigate to="/" />} />
    </Route>
  </Route>
);

const acntReviewerRoutes = (
  <Route path="/reviewer" key="reviewer">
    <Route
      path=""
      element={
        <PageContentReviewerDashboard>
          <Navigate to="dashboard" />
        </PageContentReviewerDashboard>
      }
    />
    <Route
      path="dashboard"
      element={
        <PageContentReviewerDashboard>
          <ReviewerDashboard />
        </PageContentReviewerDashboard>
      }
    />
    <Route path="application/:applicationId" element={<ReviewerApplicationDetails />} />
    <Route path="/reviewer/application/preview/:fileName/:docId"
      element={<PageContent><ReviewerFilesPreview /></PageContent>} />
    <Route
      path="*"
      element={
        <PageContentReviewerDashboard>
          <ErrorMessageComp route="/reviewer/dashboard" />
        </PageContentReviewerDashboard>
      }
    />
  </Route>
);

const acntFinanceRoutes = (
  <Route path="/" key="associate">
    <Route path="" element={<Home />} />
    <Route path="/application-details" element={<PageContent><ApplicationDetailsPage /></PageContent>} />
    <Route path="/newgrant-application" element={<PageContent><NewGrantApplicationPage /></PageContent>} />
    <Route path="/review-submit" element={<PageContent><NewGrantReviewSubmitPage /></PageContent>} />
    <Route path="/confirmation" element={<PageContent><ConfirmationPage /></PageContent>} />
    <Route path="/grantdetails" element={<PageContent><GrantDetailsPage /></PageContent>} />
    <Route path="/preferences" element={<PageContent><CommunicationPreferences type="editGrant" /></PageContent>} />
    <Route path="finance-report" element={<PageContent><FinanceReport /></PageContent>} />
    <Route
      path="/reviewer/dashboard"
      element={
        <PageContentReviewerDashboard>
          <ReviewerDashboard />
        </PageContentReviewerDashboard>
      }
    />
    <Route path="/reviewer/application/:applicationId" element={<ReviewerApplicationDetails />} />
    <Route path="/reviewer/application/preview/:fileName/:docId"
      element={<PageContent><ReviewerFilesPreview /></PageContent>} />
    <Route path="associate">
      <Route path="*" element={<PageContent><Navigate to="/" /></PageContent>} />
    </Route>
  </Route>
);

const acntAdminRoutes = (
  <Route path="/admin" key="admin">
    <Route index element={<PageContent><AdminHome /></PageContent>} />
    <Route path="configure-faq" element={<PageContent><AdminHome /></PageContent>} />
    <Route path="configure-hardship" element={<PageContent><CreateHardship /></PageContent>} />
    <Route path="configure-grant-request" element={<PageContent><CreateGrantRequest /></PageContent>} />
    <Route path="configure-disaster" element={<PageContent><CreateDisaster /></PageContent>} />
    <Route path="acnt-metrics-dashboard" element={<PageContent><AcntMetricsDashboard /></PageContent>} />
    <Route path="finance-report" element={<PageContent><FinanceReport /></PageContent>} />
    <Route path="*" element={<PageContent><ErrorMessageComp route="/admin" /></PageContent>} />
  </Route>
);

const acntSupportTier1Routes = (
  <Route path="/" key="associate">
    <Route path="" element={<PageContent><Home /></PageContent>} />
    <Route path="/application-details" element={<PageContent><ApplicationDetailsPage /></PageContent>} />
    <Route path="/newgrant-application" element={<PageContent><NewGrantApplicationPage /></PageContent>} />
    <Route path="/review-submit" element={<PageContent><NewGrantReviewSubmitPage /></PageContent>} />
    <Route path="/confirmation" element={<PageContent><ConfirmationPage /></PageContent>} />
    <Route path="/grantdetails" element={<PageContent><GrantDetailsPage /></PageContent>} />
    <Route path="/preferences" element={<PageContent><CommunicationPreferences type="editGrant" /></PageContent>} />
    <Route
      path="/reviewer/dashboard"
      element={
        <PageContentReviewerDashboard>
          <ReviewerDashboard />
        </PageContentReviewerDashboard>
      }
    />
    <Route path="/reviewer/application/:applicationId" element={<ReviewerApplicationDetails />} />
    <Route path="/reviewer/application/preview/:fileName/:docId"
      element={<PageContent><ReviewerFilesPreview /></PageContent>} />
    <Route path="associate">
      <Route path="*" element={<Navigate to="/" />} />
    </Route>
  </Route>
);

const NavRouter: React.FunctionComponent = () => {
  const url = window.location.href;
  const activeRoutes = [
    getVerifiedRole([ACNT_ASSOCIATE_ROLE], url) && acntAssociateRoutes,
    getVerifiedRole([ACNT_REVIEWER_ROLE], url) && acntReviewerRoutes,
    getVerifiedRole([ACNT_ADMIN_ROLE], url) && acntAdminRoutes,
    getVerifiedRole([ACNT_FINANCE_ROLE], url) && acntFinanceRoutes,
    getVerifiedRole([ACNT_SUPPORT_TIER_1], url) && acntSupportTier1Routes,
    getVerifiedRole([ACNT_SUPPORT_TIER_2], url) && acntSupportTier1Routes,
  ];
  return (
    <React.Suspense fallback={<SkeletonText lines={7} />}>
      <Chat location={useLocation().pathname} />
      <Routes>
        <Route path="/" element={<RouteToHomePage />} />
        <Route path="/health" element={<PageContent><Health /></PageContent>} />
        selection page
        <Route path="/not-found" element={<PageContent><NotFoundPage /></PageContent>} />
        {activeRoutes}
        <Route path="*" element={<PageContent><ErrorMessageComp route="/" /></PageContent>} />
      </Routes>
    </React.Suspense>
  );
};

const Footer = () => {
  return (
    <footer className="footer-home-screen">
      <div className="footer-content">
        <span>© {new Date().getFullYear()} Walmart, Inc.</span>
        <span>
          <a
            href="https://corporate.walmart.com/privacy-security/walmart-associate-information-privacy-notice"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </span>
      </div>
    </footer>
  );
};

const App: React.FunctionComponent = () => {
  const queryClient = useQueryClient();

  // When the user changes language, refetch all active queries so any
  // server-side translated content is reloaded in the new language.
  React.useEffect(() => {
    const handleLanguageChange = () => {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== 'userPreliminaryCheck',
      });
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [queryClient]);

  return (
    <div>
      <TopBar />
        <NavRouter />
      <Footer />
    </div>
  );
};

export default App;
